import { useEffect, useState, useMemo, useCallback } from 'react';

import { useLocalStorageJSON } from 'common/hooks';
import { NavigationAnnouncement } from 'modules/experience-core';

import { useGetNavigationAnnouncements, navigationAnnouncementIdsList } from './useGetNavigationAnnouncement';

type NavigationAnnouncementId = (typeof navigationAnnouncementIdsList)[number];

export const useShowNavigationAnnouncement = () => {
  const navigationAnnouncements = useGetNavigationAnnouncements();
  const [visibleNavigationAnnouncement, setVisibleNavigationAnnouncement] = useState<
    NavigationAnnouncement<NavigationAnnouncementId> | undefined
  >(undefined);
  const [shownNavigationAnnouncements, setShownNavigationAnnouncements] = useLocalStorageJSON<
    Partial<Record<NavigationAnnouncementId, boolean>>
  >('shownNavigationAnnouncements', {});

  const remainingNavigationAnnouncements = useMemo(() => {
    return navigationAnnouncements.filter(
      navigationAnnouncement => shownNavigationAnnouncements[navigationAnnouncement.id] === undefined,
    );
  }, [navigationAnnouncements, shownNavigationAnnouncements]);

  useEffect(() => {
    if (remainingNavigationAnnouncements.length > 0 && !visibleNavigationAnnouncement) {
      setVisibleNavigationAnnouncement(remainingNavigationAnnouncements[0]);
    }
  }, [remainingNavigationAnnouncements, visibleNavigationAnnouncement]);

  const dismiss = useCallback(() => {
    if (visibleNavigationAnnouncement) {
      setShownNavigationAnnouncements({ ...shownNavigationAnnouncements, [visibleNavigationAnnouncement.id]: true });
      setVisibleNavigationAnnouncement(undefined);
    }
  }, [visibleNavigationAnnouncement, shownNavigationAnnouncements, setShownNavigationAnnouncements]);

  return useMemo(() => {
    return visibleNavigationAnnouncement
      ? {
          text: visibleNavigationAnnouncement.text,
          navigationKey: visibleNavigationAnnouncement.navigationKey,
          tag: visibleNavigationAnnouncement.tag,
          dismiss,
        }
      : undefined;
  }, [visibleNavigationAnnouncement, dismiss]);
};
